/**
 * Возврат клавиатуры доске.
 *
 * Горячие клавиши (Ctrl+Z, Ctrl+C, Ctrl+V, Delete) слушает окно доски. Если
 * фокус ушёл в окно звонка — а это <iframe>, отдельный документ, — нажатия
 * до доски не доходят вовсе: окно ловит ноль событий. Само по себе это
 * чинится щелчком по странице, но инструменты доски гасят pointerdown
 * (ev.preventDefault), чтобы не выделялся текст и не прокручивалась
 * страница, — а вместе с ним гаснет и перевод фокуса. Поэтому клавиши
 * «пропадали» до первого нажатия по кнопке панели: доска рисует, а Ctrl+Z
 * уходит в звонок.
 *
 * Проверено на стенде: при погашенном pointerdown активным остаётся IFRAME
 * и окно не получает ни одного keydown; после blur() нажатия приходят снова.
 */

/** Поля, у которых фокус забирать нельзя: там сейчас правят текст. */
const EDITABLE =
  "input, textarea, select, [contenteditable='true'], .wb-text-overlay-editor, .cm-editor";

/** Карточки виджетов: щелчок внутри своей карточки — продолжение работы в ней. */
const WIDGET_CARD = "[data-object-id]";

/**
 * Отпускать ли фокус, когда палец или мышь легли на доску.
 *
 * `active` — что держит фокус сейчас, `target` — куда нажали.
 */
export function shouldReleaseFocus(
  active: Element | null | undefined,
  target: Element | null | undefined
): boolean {
  if (!active) return false;
  if (active.tagName === "BODY" || active.tagName === "HTML") return false;
  // Правят надпись на доске — поле набора закрывается само, по своим правилам.
  if (active.closest(".wb-text-overlay-editor")) return false;
  // Нажали в поле — фокус туда и идёт.
  if (target && target.closest(EDITABLE)) return false;
  // Нажали по кнопке той же карточки виджета («Выполнить» в SQL) — работа
  // в виджете продолжается, фокус оставляем.
  const card = active.closest(WIDGET_CARD);
  if (card && target && card.contains(target)) return false;
  // Всё прочее — щелчок по самой доске. Раньше фокус оставался в редакторе
  // SQL или кода, если до этого печатали там: доска считала, что человек
  // всё ещё набирает, и Ctrl+V молча уходил в виджет, часто уже за экраном.
  return true;
}

/**
 * Отпускать ли фокус, когда мышь просто вернулась на доску — без щелчка.
 *
 * Типичный урок: нажали «выключить микрофон» в звонке, повели мышь обратно
 * на доску и сразу Ctrl+V. Щелчка по доске не было, фокус остался в окне
 * звонка, и вставка молчала. Здесь забираем фокус только у <iframe>: поле
 * на самой странице (название фрейма, поиск) от движения мыши не теряем.
 * Набирать текст в звонке это не мешает — пока мышь стоит, событий нет.
 */
export function shouldReleaseFocusOnHover(active: Element | null | undefined): boolean {
  return Boolean(active && active.tagName === "IFRAME");
}
